/**
 * Thesis Fit — Archetype Presets
 *
 * Each archetype defines overrides for key dimensions.
 * `makeWeights()` distributes the remainder equally among
 * non-overridden dimensions. When DIMENSION_ORDER grows
 * (13 → 15), the remainder auto-distributes to N+2 dimensions.
 */

import type { ScoringDimension } from '@/lib/scoring/methodology';
import { DIMENSION_ORDER } from '@/lib/scoring/methodology';
import type { ThesisArchetype } from './types';
import { WEIGHT_CONSTRAINTS } from './types';

// =============================================================================
// Weight Builder
// =============================================================================

/**
 * Build a full weight record from sparse overrides.
 * Sets explicit overrides, distributes remainder equally across remaining dimensions.
 * Clamps all values to [min, max] and normalizes to sum = 1.0.
 */
export function makeWeights(
  overrides: Partial<Record<ScoringDimension, number>> = {},
): Record<ScoringDimension, number> {
  const { min, max } = WEIGHT_CONSTRAINTS;
  const overriddenDims = new Set(Object.keys(overrides) as ScoringDimension[]);
  const overrideTotal = Object.values(overrides).reduce((s, v) => s + v, 0);
  const remaining = 1.0 - overrideTotal;
  const nonOverriddenCount = DIMENSION_ORDER.length - overriddenDims.size;
  const equalShare = nonOverriddenCount > 0 ? remaining / nonOverriddenCount : 0;

  const weights = {} as Record<ScoringDimension, number>;
  for (const dim of DIMENSION_ORDER) {
    const raw = overriddenDims.has(dim) ? overrides[dim]! : equalShare;
    weights[dim] = Math.max(min, Math.min(max, raw));
  }

  // Normalize to exactly 1.0
  const total = Object.values(weights).reduce((s, v) => s + v, 0);
  if (total > 0) {
    for (const dim of DIMENSION_ORDER) {
      weights[dim] = weights[dim] / total;
    }
  }

  return weights;
}

// =============================================================================
// Archetype Definitions
// =============================================================================

const ARCHETYPE_OVERRIDES: Record<
  Exclude<ThesisArchetype, 'custom'>,
  Partial<Record<ScoringDimension, number>>
> = {
  balanced: {},  // All equal — no overrides
  traction: {
    traction: 0.18,
    'gtm-execution': 0.12,
    'financial-health': 0.10,
  },
  'tech-team': {
    team: 0.16,
    product: 0.14,
    'execution-velocity': 0.12,
  },
  defensibility: {
    defensibility: 0.16,
    competition: 0.12,
    timing: 0.10,
  },
};

/**
 * Get the weight preset for a given archetype.
 * 'custom' starts from balanced (equal weights).
 */
export function getArchetypeWeights(
  archetype: ThesisArchetype,
): Record<ScoringDimension, number> {
  if (archetype === 'custom') {
    return makeWeights(); // Equal weights
  }
  return makeWeights(ARCHETYPE_OVERRIDES[archetype]);
}

/**
 * Get all archetype IDs
 */
export const ARCHETYPE_IDS: ThesisArchetype[] = [
  'balanced',
  'traction',
  'tech-team',
  'defensibility',
  'custom',
];
